import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {Compbaser} from "../../../components/compbaser";
import {ContainerService, SceneService, StoreSelectorsService} from "../../../services";
import {ToastrService} from 'ngx-toastr';
import {TranslocoService} from '@ngneat/transloco';
import {ImportService} from "../../../services/import.service";
import {appData, popupWindow} from "../../../common";
import {MatDialog} from "@angular/material/dialog";
import {staticBgSceneTemplates} from "../../../../assets/accountTemplates/background-constants";
import {DialogPopComponent} from '../../../components/dialog-pop/dialog-pop.component';
import {IMPORT_WAIT_SECONDS} from '../scene-blocks/block-constants';
import {DialogImportingComponent} from "../../../components/dialog-importing/dialog-importing.component";
import {DialogAiResourceComponent} from "../../../components/dialog-ai-resource/dialog-ai-resource.component";
import * as _ from 'lodash';
import {Lib} from 'libs/Lib';

interface IBgImage {
    resourceId: string;
    businessId: string;
    imageThumb: string;
    width: string;
    height: string;
    src?: string;
    ext?: string;
}

@Component({
    selector: 'app-backgrounds-texts',
    templateUrl: './scene-backgrounds.component.html',
    styleUrls: ['./scene-backgrounds.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SceneBackgroundsComponent extends Compbaser implements OnInit {

    @ViewChild("importPopup", {read: TemplateRef}) importPopup: TemplateRef<any>;

    snippets = [];
    currentSnippets = [];
    color = '';
    selectedImage: IBgImage;
    imageLoading = true;
    helpModalDiag;
    imageImporting = false;
    m_colorChangedFunc;
    sceneId;
    throttle = 300;
    scrollDistance = 2;
    initialCount = 20;
    verticalMoreCount = 20;

    constructor(
        private container: ContainerService,
        private importService: ImportService,
        private cd: ChangeDetectorRef,
        protected override dialog: MatDialog,
        private toastr: ToastrService,
        private storeSelector: StoreSelectorsService,
        private translocoService: TranslocoService,
        private sceneService: SceneService
    ) {
        super(dialog);
        const bgTemplates = staticBgSceneTemplates;
        Object.keys(bgTemplates).forEach((v) => {
            this.snippets.push(bgTemplates[v]);
        });

        this.currentSnippets = this.snippets.slice(0, this.initialCount);

        this.m_colorChangedFunc = _.debounce(() => {
            this.changeSceneBackgroundColor();
        }, 555);
    }

    ngOnInit(): void {
        this.cod(
            this.storeSelector.listenSelectedSceneId()
                .subscribe((v) => {
                    this.sceneId = v;
                })
        );
    }

    /** Load more snippets when scroll down */
    loadMoreSnippets() {
        const cnt = this.currentSnippets.length;
        if (cnt < this.snippets.length) {
            this.currentSnippets.push(...this.snippets.slice(cnt, cnt + this.verticalMoreCount));
        }
        this.cd.detectChanges();
    }

    private changeSceneBackgroundColor() {
        const colorDecimal = Lib.HexToDecimal(this.color);
        this.sceneService.setSceneBackgroundColor(colorDecimal, true);
    }

    async eyeDropper() {
        try {
            // @ts-expect-error:
            const eyeDropper = new EyeDropper();
            const selectedColor = await eyeDropper.open();
            if (!selectedColor) return;
            this.color = Lib.EyeDropperConvert(selectedColor.sRGBHex);
            this.changeSceneBackgroundColor();
        } catch (err) {
            console.log('color selection cancelled');
        }
    }

    colorPickerChange(e) {
        this.m_colorChangedFunc();
    }

    clearBackground() {
        const msg = this.translocoService.translate('message.clearBackground', {}, this.translocoService.getActiveLang());
        const dialogRef = this.dialog.open(DialogPopComponent, {
            height: '200px',
            width: '400px',
            data: {name: msg, showCancel: true}
        });
        dialogRef.afterClosed().subscribe((r) => {
            if (r) {
                this.sceneService.setSceneBackgroundColor('transparent', true);
            }
        });
    }

    popupBackgroundWnd() {
        const url = `assets/background/index.html`;
        popupWindow({url, title: '_blank', w: 1200, h: 600}, 'no', 'yes');
    }

    openAIBackgroundDialog() {
        const dialogRef = this.dialog.open(DialogAiResourceComponent, {
            width: '90vw',
            maxWidth: '1200px',
            height: '90vh',
            maxHeight: '900px',
            data: {
                mediaTypeFilter: 'image'
            }
        });
    }

    showInfoBg(item: IBgImage) {
        this.imageLoading = true;
        this.cd.detectChanges();

        this.selectedImage = item;
        this.selectedImage.ext = this.selectedImage.imageThumb.split(".").pop();
        this.selectedImage.src = `${appData.cloudResourcesS3}/business${item.businessId}/resources/${item.resourceId}.${this.selectedImage.ext}`;

        this.helpModalDiag = this.dialog.open(this.importPopup, {
            width: '600px',
            height: '60%',
            data: {
                callBack: (data) => {
                    this.helpModalDiag.close();
                }
            }
        });
        this.helpModalDiag.afterClosed().subscribe(result => {
            this.imageLoading = false;
        });

        setTimeout(() => {
            if (this.imageLoading) {
                this.toastr.error(this.translocoService.translate('toast.notFoundImage', {}, this.translocoService.getActiveLang()));
                this.onClose();
            }
        }, 15000);
    }

    onLoad(e) {
        this.imageLoading = false;
        this.cd.detectChanges();
    }

    async dropImage(e) {
        if (e.isPointerOverContainer) return;
        this.selectedImage = e.item.data;
        this.selectedImage.ext = this.selectedImage.imageThumb.split(".").pop();

        try {
            await this.onImportClick();
        } catch (err) {
            console.log(err);
        }
    }

    async onImportClick() {
        if (!this.selectedImage) return;
        this.cd.markForCheck();
        this.onClose();
        // this.imageImporting = true;

        this.dialog.open(DialogImportingComponent, {
            height: '400px',
            width: '400px',
            panelClass: 'transparentDialog',
            data: {
                showCancel: true
            },
            disableClose: true
        });

        try {
            this.sceneService.selectRootScene();
            const newResourceId = await this.importService.importResource(this.selectedImage.businessId, this.selectedImage.resourceId);
            this.sceneService.setBackgroundImage({
                resId: newResourceId,
                resType: this.selectedImage.ext,
                showConfirmDialog: false
            });
        } catch {
            this.toastr.error(this.translocoService.translate('toast.notFoundImage', {}, this.translocoService.getActiveLang()));
        } finally {
            setTimeout(() => {
                this.onClose();
                this.cd.detectChanges();
            }, IMPORT_WAIT_SECONDS);
        }

        // setTimeout(() => {
        //     this.imageImporting = false;
        //     this.onClose();
        //     this.cd.detectChanges();
        // }, IMPORT_WAIT_SECONDS);
    }

    onClose() {
        this.imageLoading = false;
        this.dialog.closeAll();
        // if (this.helpModalDiag) {
        //     this.helpModalDiag.close();
        // }
    }
}
